import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Token from localStorage
const token = localStorage.getItem('token');

/**
 * Egnyte Password Grant OAuth:
 * - mcName must be "Egnyte"
 * - Uses password grant flow (no redirect needed)
 * - Backend handles token acquisition via synctrack/egnyte/token?mcId={ctdId}
 */
export const getEgnyteCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    console.log('🔍 Egnyte: Starting password grant OAuth for connectorId:', connectorId, 'mcId:', mcId);

    // 1) Find Egnyte master connector (master connector name is "Egnyte Storage")
    const egnyteConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() ||
       mc.mcId?.toString() === mcId?.toString()) &&
      (mc.mcName === 'Egnyte Storage' || mc.mcName === 'Egnyte')
    );
    if (!egnyteConnector) {
      throw new Error('Egnyte master connector not found');
    }

    // 2) Fetch connector parameters from the API endpoint using connector instance ID (ctd_id)
    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!paramResponse.ok) {
      throw new Error('Failed to fetch connector parameters');
    }
    const connectorParams = await paramResponse.json();

    console.log('🔍 Egnyte: Fetched connector parameters');
    console.log('🔍 Egnyte: Parameter names found:', connectorParams.map(p => p.scpName));

    // Verify required parameters exist (backend will decrypt them)
    const hasClientId = connectorParams.some(p => 
      p.scpName?.toLowerCase() === 'client_id' || p.scpName?.toLowerCase() === 'clientid'
    );
    const hasClientSecret = connectorParams.some(p => 
      p.scpName?.toLowerCase() === 'client_secret' || p.scpName?.toLowerCase() === 'clientsecret'
    );
    const hasUsername = connectorParams.some(p => 
      p.scpName?.toLowerCase() === 'username'
    );
    const hasPassword = connectorParams.some(p => 
      p.scpName?.toLowerCase() === 'password'
    );

    if (!hasClientId || !hasClientSecret || !hasUsername || !hasPassword) {
      console.error('❌ Egnyte: Missing required parameters. Available params:', 
        connectorParams.map(p => ({ name: p.scpName, hasValue: !!p.scpValue })));
      throw new Error('Required parameters not found. Need: client_id, client_secret, username, password');
    }

    console.log('✅ Egnyte: All required parameters found');

    // 3) Call backend token endpoint (backend handles password grant OAuth)
    // Backend will decrypt credentials and make the OAuth request
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/egnyte/token?mcId=${connectorId}`;

    console.log('🔄 Egnyte: Calling backend token endpoint...');
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Egnyte: Token acquisition error response:', errorText);
      throw new Error(`Failed to get Egnyte token: ${response.status} - ${errorText}`);
    }

    const tokenData = await response.json();
    console.log('✅ Egnyte: Token acquisition successful');
    console.log('🔍 Egnyte: Response data:', {
      hasAccessToken: !!tokenData.access_token,
      hasRefreshToken: !!tokenData.refresh_token,
      refreshToken: tokenData.refresh_token ? `${tokenData.refresh_token.substring(0, 20)}...` : 'null'
    });

    // Check for error in response
    if (tokenData.error) {
      throw new Error(tokenData.message || 'Failed to get Egnyte token');
    }

    // 4) Update connector with token data
    // Note: Backend already saves tokens via saveTokenToDatabase, but we update here to ensure consistency
    const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;

    // Fetch current connector (after backend save, so it should have the refresh token)
    const connectorResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!connectorResponse.ok) {
      throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);
    }
    const connectorData = await connectorResponse.json();

    // Calculate token expiry dates
    const expiresIn = parseInt(tokenData.expires_in, 10) || 3600;
    const refreshTokenExpiry = tokenData.refresh_token 
      ? new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString() // 180 days
      : null;

    // Get refresh URL from master connector (mcRefreshUrl or fallback to mcTokenurl)
    // This should be the Egnyte API endpoint, not the backend endpoint
    const refreshUrl = egnyteConnector.mcRefreshUrl || egnyteConnector.mcTokenurl || null;

    // IMPORTANT: Always use refresh_token from backend response (tokenData.refresh_token)
    // Backend response contains the actual refresh token from Egnyte API
    // If it's null in response, it means Egnyte didn't provide one
    const refreshTokenValue = tokenData.refresh_token || null;
    
    console.log('🔍 Egnyte: Refresh token from backend response:', 
      refreshTokenValue ? `${refreshTokenValue.substring(0, 20)}...` : 'null (not provided by Egnyte)');
    console.log('🔍 Egnyte: Existing refresh token in connector:', 
      connectorData.refreshToken ? `${connectorData.refreshToken.substring(0, 20)}...` : 'null');
    
    // Prepare updated connector data
    const updatedConnectorData = {
      ...connectorData,
      token: tokenData.access_token,
      refreshToken: refreshTokenValue, // Always use refresh_token from backend response
      tokenExpiry: new Date(Date.now() + expiresIn * 1000).toISOString(),
      secondaryToken: null,
      secondaryTokenExpiry: null,
      refreshUrl: refreshUrl, // Use master connector refresh URL (Egnyte API endpoint)
      refreshTokenExpiry: refreshTokenExpiry,
      status: 'Connected',
      connectedTime: new Date().toISOString()
    };
    
    console.log('🔍 Egnyte: Final refreshToken in update request:', 
      updatedConnectorData.refreshToken ? `${updatedConnectorData.refreshToken.substring(0, 20)}...` : 'null');

    // Update connector
    const updateResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updatedConnectorData)
    });

    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      console.error('❌ Egnyte: Update connector error:', errorText);
      throw new Error(`Failed to update connector with tokens: ${updateResponse.status}`);
    }

    console.log('✅ Egnyte: Connector updated successfully with tokens');
    
    // Return token data (password grant flow - no redirect needed)
    return {
      token: tokenData.access_token,
      refreshToken: refreshTokenValue,
      tokenExpiry: updatedConnectorData.tokenExpiry,
      refreshTokenExpiry: refreshTokenExpiry,
      status: 'Connected'
    };
  } catch (error) {
    console.error('❌ Egnyte: Error getting credentials:', error);
    throw error;
  }
};

// Helper: exchange refresh token for new access token
export const exchangeEgnyteAuthCodeForToken = async (authCode, clientId, clientSecret, mcId, redirectUri) => {
  // This function is kept for backward compatibility but is not used in password grant flow
  // Password grant doesn't use authorization codes
  console.warn('⚠️ Egnyte: exchangeEgnyteAuthCodeForToken called but not used in password grant flow');
  throw new Error('Egnyte uses password grant OAuth, not authorization code flow');
};

